import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Upload, Download } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ProductServiceImport } from "../components/ProductServiceImport";
import { PageHeader, DataTable, Td, FormDialog, Field, TextInput, SelectInput, ActionButton, EmptyState } from "../components/common";

const EMPTY = { name: "", type: "", rate: "", unit_of_measure: "", resource_types: "", description: "", customer_id: "" };

export default function ProductServiceItems() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  const load = () => api.get("/product-service-items").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const distinctOptions = (key) => [...new Set(items.map((i) => String(i[key] || "").trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
  const typeOptions = distinctOptions("type");
  const uomOptions = distinctOptions("unit_of_measure");
  const resourceOptions = distinctOptions("resource_types");
  const customerOptions = customers.map((c) => ({ value: c.id, label: c.name }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name || "", type: it.type || "", rate: it.rate ?? "", unit_of_measure: it.unit_of_measure || "", resource_types: it.resource_types || "", description: it.description || "", customer_id: it.customer_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: form.name.trim(), type: form.type || null, unit_of_measure: form.unit_of_measure || null,
      resource_types: form.resource_types || null, description: form.description || null,
      customer_id: form.customer_id || null,
      rate: form.rate === "" ? null : Number(form.rate) };
    try {
      if (editing) { await api.patch(`/product-service-items/${editing.id}`, payload); toast.success("Item updated"); }
      else { await api.post("/product-service-items", payload); toast.success("Item added"); }
      setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const headers = ["name", "type", "rate", "unit_of_measure", "resource_types", "description", "customer_id", "customer_name"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...items.map((it) => headers.map((h) => esc(it[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "product_service_items.csv";
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`Exported ${items.length} items`);
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter((it) => [it.name, it.type, it.customer_name, it.resource_types].some((v) => String(v || "").toLowerCase().includes(q))) : items;

  return (
    <div data-testid="product-service-page">
      <PageHeader title="Product & Service Items" subtitle="Shared catalog of billable products, services and accessorial charges" testId="product-service-header">
        <ActionButton testId="product-service-export-button" variant="secondary" onClick={exportCsv}><Download size={16} /> Export CSV</ActionButton>
        <ActionButton testId="product-service-import-button" variant="secondary" onClick={() => setImportOpen(true)}><Upload size={16} /> Import CSV</ActionButton>
        <ActionButton testId="product-service-create-button" onClick={openCreate}><Plus size={16} /> New Item</ActionButton>
      </PageHeader>

      <div className="mb-4">
        <TextInput testId="product-service-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, type, customer…" />
        <p className="mt-1 text-xs text-slate-500" data-testid="product-service-count">{filtered.length} of {items.length} items</p>
      </div>

      {items.length === 0 ? (
        <EmptyState message="No product or service items yet" testId="product-service-empty" />
      ) : (
        <DataTable
          testId="product-service-table"
          columns={["Name", "Type", "Rate", "Unit", "Resource Types", "Customer", ""]}
          rows={filtered.slice(0, 2000)}
          renderRow={(it) => (
            <tr key={it.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`product-service-row-${it.id}`}>
              <Td className="font-medium text-slate-100 max-w-md truncate" title={it.name}>{it.name}</Td>
              <Td className="text-slate-400">{it.type || "—"}</Td>
              <Td className="text-slate-300">{it.rate != null ? it.rate : "—"}</Td>
              <Td className="text-slate-400">{it.unit_of_measure || "—"}</Td>
              <Td className="text-slate-400">{it.resource_types || "—"}</Td>
              <Td className="text-slate-400" data-testid={`product-service-customer-${it.id}`}>{it.customer_name || "—"}</Td>
              <Td>
                <button data-testid={`product-service-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200"><Pencil size={14} /></button>
              </Td>
            </tr>
          )}
        />
      )}

      <ProductServiceImport open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Item" : "New Item"} testId="product-service-form-dialog">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name"><TextInput testId="product-service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Customer">
            <SelectInput testId="product-service-customer-input" value={form.customer_id}
              onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              options={customerOptions} placeholder="— No customer —" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type">
              <SelectInput testId="product-service-type-input" value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                options={typeOptions} placeholder="Select type…" />
            </Field>
            <Field label="Rate"><TextInput testId="product-service-rate-input" type="number" min="0" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Unit of Measure">
              <SelectInput testId="product-service-unit-input" value={form.unit_of_measure}
                onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}
                options={uomOptions} placeholder="Select unit…" />
            </Field>
            <Field label="Resource Types">
              <SelectInput testId="product-service-resource-input" value={form.resource_types}
                onChange={(e) => setForm({ ...form, resource_types: e.target.value })}
                options={resourceOptions} placeholder="Select resource type…" />
            </Field>
          </div>
          <Field label="Description"><TextInput testId="product-service-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <ActionButton testId="product-service-submit-button" type="submit" disabled={busy} className="w-full justify-center">{busy ? "Saving…" : editing ? "Save Changes" : "Add Item"}</ActionButton>
        </form>
      </FormDialog>
    </div>
  );
}
